import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Chip,
  TextField,
  Button,
  IconButton,
  Divider,
  Alert,
  List,
  ListItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CommentIcon from '@mui/icons-material/Comment';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { useTranslation } from 'react-i18next';
import CategoriesManagement from './CategoriesManagement';

const normalizeSynonyms = (synonyms = []) => (
  (Array.isArray(synonyms) ? synonyms : [])
    .map((synonym, index) => {
      if (typeof synonym === 'string') {
        return {
          id: `api-${index}-${synonym}`,
          term: synonym,
          language: 'und',
          context: '',
          addedBy: 'System',
          addedDate: '',
          approved: true,
          approvedBy: null,
          likes: 0,
          dislikes: 0,
          likedBy: [],
          dislikedBy: [],
          comments: [],
        };
      }

      if (!synonym || typeof synonym !== 'object') {
        return null;
      }

      return {
        id: synonym.id || `api-${index}-${synonym.term || synonym.name || 'synonym'}`,
        term: synonym.term || synonym.name || '',
        language: synonym.language || 'und',
        context: synonym.context || '',
        addedBy: synonym.addedBy || 'System',
        addedDate: synonym.addedDate || '',
        approved: synonym.approved ?? true,
        approvedBy: synonym.approvedBy || null,
        likes: synonym.likes || 0,
        dislikes: synonym.dislikes || 0,
        likedBy: synonym.likedBy || [],
        dislikedBy: synonym.dislikedBy || [],
        comments: synonym.comments || [],
      };
    })
    .filter(Boolean)
);

const TAGS_SYNONYMS_DETAIL_COPY = {
  de: {
    categories: 'Kategorien',
    synonyms: 'Synonyme',
    synonymsHelp: 'Alternative Bezeichnungen für diesen Parameter (z.B. Abkürzungen, andere Sprachen)',
    approvedSynonyms: 'Bestätigte Synonyme',
    likeSynonym: 'Synonym liken',
    dislikeSynonym: 'Synonym disliken',
    addComment: 'Kommentar hinzufügen',
    deleteSynonym: 'Synonym löschen',
    addedByOn: 'Hinzugefügt von {{user}} am {{date}}',
    approvedBy: 'Genehmigt von {{user}}',
    sendComment: 'Kommentar senden',
    suggestSynonym: 'Synonym vorschlagen',
    pendingSuggestions: 'Ausstehende Vorschläge',
    pendingHelp: 'Diese Synonyme warten noch auf Bestätigung durch Data Stewards oder die Community',
    context: 'Kontext',
    suggestedBy: 'Vorgeschlagen von {{user}}',
    approve: 'Genehmigen',
    reject: 'Ablehnen',
    editSynonym: 'Synonym bearbeiten',
    synonymField: 'Synonym',
    languageField: 'Sprache',
    optionalContextField: 'Kontext (optional)',
    save: 'Speichern',
    cancel: 'Abbrechen',
    likeOwnSuggestion: 'Eigenen Vorschlag liken',
    dislikeOwnSuggestion: 'Eigenen Vorschlag disliken',
    editOwnSuggestion: 'Eigenen Vorschlag bearbeiten',
    deleteOwnSuggestion: 'Eigenen Vorschlag löschen',
    proposeSynonym: 'Synonym vorschlagen',
    synonymPlaceholder: 'Alternative Bezeichnung eingeben...',
    contextPlaceholder: 'z.B. Klinische Abkürzung, Umgangssprachlich...',
    submitHelp: 'Ihr Vorschlag wird zur Prüfung an das Data Steward Team gesendet. Bei ausreichender Community-Unterstützung kann das Synonym automatisch genehmigt werden.',
    submit: 'Vorschlagen',
    deleteTitle: 'Synonym löschen?',
    deletePrompt: 'Möchten Sie dieses bestätigte Synonym wirklich löschen?',
    languageLabel: 'Sprache: {{language}}',
    addedBy: 'Hinzugefügt von: {{user}} • {{date}}',
    cannotUndo: 'Diese Aktion kann nicht rückgängig gemacht werden.',
  },
  en: {
    categories: 'Categories',
    synonyms: 'Synonyms',
    synonymsHelp: 'Alternative names for this parameter (e.g. abbreviations, other languages)',
    approvedSynonyms: 'Approved synonyms',
    likeSynonym: 'Like synonym',
    dislikeSynonym: 'Dislike synonym',
    addComment: 'Add comment',
    deleteSynonym: 'Delete synonym',
    addedByOn: 'Added by {{user}} on {{date}}',
    approvedBy: 'Approved by {{user}}',
    sendComment: 'Send comment',
    suggestSynonym: 'Suggest synonym',
    pendingSuggestions: 'Pending suggestions',
    pendingHelp: 'These synonyms are still waiting for confirmation by data stewards or the community',
    context: 'Context',
    suggestedBy: 'Suggested by {{user}}',
    approve: 'Approve',
    reject: 'Reject',
    editSynonym: 'Edit synonym',
    synonymField: 'Synonym',
    languageField: 'Language',
    optionalContextField: 'Context (optional)',
    save: 'Save',
    cancel: 'Cancel',
    likeOwnSuggestion: 'Like own suggestion',
    dislikeOwnSuggestion: 'Dislike own suggestion',
    editOwnSuggestion: 'Edit own suggestion',
    deleteOwnSuggestion: 'Delete own suggestion',
    proposeSynonym: 'Suggest synonym',
    synonymPlaceholder: 'Enter an alternative label...',
    contextPlaceholder: 'e.g. Clinical abbreviation, colloquial term...',
    submitHelp: 'Your suggestion will be sent to the data steward team for review. With enough community support, the synonym can be approved automatically.',
    submit: 'Submit suggestion',
    deleteTitle: 'Delete synonym?',
    deletePrompt: 'Do you really want to delete this approved synonym?',
    languageLabel: 'Language: {{language}}',
    addedBy: 'Added by: {{user}} • {{date}}',
    cannotUndo: 'This action cannot be undone.',
  },
  pt: {
    categories: 'Categorias',
    synonyms: 'Sinónimos',
    synonymsHelp: 'Designações alternativas para este parâmetro (ex.: abreviaturas, outras línguas)',
    approvedSynonyms: 'Sinónimos aprovados',
    likeSynonym: 'Gostar do sinónimo',
    dislikeSynonym: 'Não gostar do sinónimo',
    addComment: 'Adicionar comentário',
    deleteSynonym: 'Eliminar sinónimo',
    addedByOn: 'Adicionado por {{user}} em {{date}}',
    approvedBy: 'Aprovado por {{user}}',
    sendComment: 'Enviar comentário',
    suggestSynonym: 'Sugerir sinónimo',
    pendingSuggestions: 'Sugestões pendentes',
    pendingHelp: 'Estes sinónimos ainda aguardam confirmação pelos data stewards ou pela comunidade',
    context: 'Contexto',
    suggestedBy: 'Sugerido por {{user}}',
    approve: 'Aprovar',
    reject: 'Rejeitar',
    editSynonym: 'Editar sinónimo',
    synonymField: 'Sinónimo',
    languageField: 'Idioma',
    optionalContextField: 'Contexto (opcional)',
    save: 'Guardar',
    cancel: 'Cancelar',
    likeOwnSuggestion: 'Gostar da própria sugestão',
    dislikeOwnSuggestion: 'Não gostar da própria sugestão',
    editOwnSuggestion: 'Editar a própria sugestão',
    deleteOwnSuggestion: 'Eliminar a própria sugestão',
    proposeSynonym: 'Sugerir sinónimo',
    synonymPlaceholder: 'Introduza uma designação alternativa...',
    contextPlaceholder: 'ex.: Abreviatura clínica, termo coloquial...',
    submitHelp: 'A sua sugestão será enviada para revisão pela equipa de data stewards. Com apoio suficiente da comunidade, o sinónimo pode ser aprovado automaticamente.',
    submit: 'Enviar sugestão',
    deleteTitle: 'Eliminar sinónimo?',
    deletePrompt: 'Pretende realmente eliminar este sinónimo aprovado?',
    languageLabel: 'Idioma: {{language}}',
    addedBy: 'Adicionado por: {{user}} • {{date}}',
    cannotUndo: 'Esta ação não pode ser anulada.',
  },
};

/**
 * TagsSynonymsDetail Component
 * Comprehensive tags and synonyms management section
 * Allows viewing, adding, and managing tags and their synonyms
 *
 * @param {object} props
 * @param {array} props.tags - Current tags for the parameter
 * @param {array} props.tagDetails - Structured tag entries for the parameter
 * @param {array} props.synonyms - Current synonyms for the parameter
 * @param {array} props.availableTags - All available tags in the system
 * @param {function} props.onAddTag - Callback when tag is added
 * @param {function} props.onApproveTag - Callback when pending tag is approved
 * @param {function} props.onRejectTag - Callback when pending tag is rejected
 * @param {function} props.onVoteTag - Callback when a tag is voted on
 * @param {function} props.onCommentTag - Callback when a tag receives a comment
 * @param {function} props.onRemoveTag - Callback when tag is removed
 * @param {function} props.onAddSynonym - Callback when synonym is added
 * @param {function} props.onApproveSynonym - Callback when pending synonym is approved
 * @param {function} props.onRejectSynonym - Callback when pending synonym is rejected
 * @param {function} props.onVoteSynonym - Callback when a synonym is voted on
 * @param {function} props.onCommentSynonym - Callback when a synonym receives a comment
 * @param {function} props.onRemoveSynonym - Callback when synonym is removed
 * @param {string} props.currentUser - Current user name
 * @param {string} props.userRole - User role (user, data_steward, admin)
 */
const TagsSynonymsDetail = ({
  tags = [],
  tagDetails = [],
  synonyms = [],
  availableTags = [],
  onAddTag,
  onApproveTag,
  onRejectTag,
  onVoteTag,
  onCommentTag,
  onRemoveTag,
  onAddSynonym,
  onApproveSynonym,
  onRejectSynonym,
  onVoteSynonym,
  onCommentSynonym,
  onRemoveSynonym,
  currentUser = 'Current User',
  userRole = 'user'
}) => {
  const { i18n } = useTranslation();
  const [newSynonym, setNewSynonym] = useState('');
  const [showAddSynonymDialog, setShowAddSynonymDialog] = useState(false);
  const [synonymLanguage, setSynonymLanguage] = useState('de');
  const [synonymContext, setSynonymContext] = useState('');
  const [commentText, setCommentText] = useState({});
  const [showCommentInput, setShowCommentInput] = useState({});

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [synonymToDelete, setSynonymToDelete] = useState(null);

  // Edit state for own synonyms
  const [editingSynonymId, setEditingSynonymId] = useState(null);
  const [editSynonymData, setEditSynonymData] = useState({ term: '', language: 'de', context: '' });

  const [localSynonyms, setLocalSynonyms] = useState(() => normalizeSynonyms(synonyms));
  const [pendingSynonymSuggestions, setPendingSynonymSuggestions] = useState([]);
  const language = i18n.resolvedLanguage?.startsWith('pt')
    ? 'pt'
    : i18n.resolvedLanguage?.startsWith('en')
      ? 'en'
      : 'de';
  const copy = TAGS_SYNONYMS_DETAIL_COPY[language];
  const locale = language === 'pt' ? 'pt-PT' : language === 'en' ? 'en-US' : 'de-DE';

  useEffect(() => {
    const normalizedSynonyms = normalizeSynonyms(synonyms);
    setLocalSynonyms(normalizedSynonyms);
    setPendingSynonymSuggestions(
      normalizedSynonyms.filter(synonym => !synonym.approved && synonym.addedBy !== currentUser)
    );
  }, [synonyms, currentUser]);

  const handleAddSynonym = () => {
    if (newSynonym.trim()) {
      const newSyn = {
        id: Date.now(),
        term: newSynonym.trim(),
        language: synonymLanguage,
        context: synonymContext.trim(),
        addedBy: currentUser,
        addedDate: new Date().toISOString().split('T')[0],
        approved: false,
        approvedBy: null,
        likes: 0,
        dislikes: 0,
        likedBy: [],
        dislikedBy: [],
        comments: []
      };
      setLocalSynonyms([...localSynonyms, newSyn]);
      setNewSynonym('');
      setSynonymContext('');
      setShowAddSynonymDialog(false);

      if (onAddSynonym) {
        onAddSynonym(newSyn);
      }
    }
  };

  const handleRemoveSynonym = (synonymId) => {
    setLocalSynonyms(localSynonyms.filter(s => s.id !== synonymId));
    if (onRemoveSynonym) {
      onRemoveSynonym(synonymId);
    }
  };

  const handleApproveSynonym = async (suggestionId) => {
    if (onApproveSynonym) {
      onApproveSynonym(suggestionId);
      return;
    }
    const suggestion = pendingSynonymSuggestions.find(s => s.id === suggestionId);
    if (suggestion) {
      const approved = {
        id: suggestion.id,
        term: suggestion.term,
        language: suggestion.language,
        context: suggestion.context,
        addedBy: suggestion.suggestedBy,
        addedDate: new Date().toISOString().split('T')[0],
        approved: true,
        approvedBy: currentUser
      };
      setLocalSynonyms([...localSynonyms, approved]);
      setPendingSynonymSuggestions(pendingSynonymSuggestions.filter(s => s.id !== suggestionId));
    }
  };

  const handleRejectSuggestion = async (suggestionId) => {
    if (onRejectSynonym) {
      onRejectSynonym(suggestionId);
      return;
    }
    setPendingSynonymSuggestions(pendingSynonymSuggestions.filter(s => s.id !== suggestionId));
  };

  // Delete handlers for confirmed synonyms
  const handleDeleteConfirmedSynonymClick = (synonym) => {
    setSynonymToDelete(synonym);
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = () => {
    if (synonymToDelete) {
      setLocalSynonyms(localSynonyms.filter(s => s.id !== synonymToDelete.id));
      if (onRemoveSynonym) {
        onRemoveSynonym(synonymToDelete.id);
      }
    }
    setDeleteDialogOpen(false);
    setSynonymToDelete(null);
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setSynonymToDelete(null);
  };

  // Handlers for confirmed synonyms
  const handleLikeConfirmedSynonym = async (synonymId) => {
    if (onVoteSynonym) {
      onVoteSynonym(synonymId, 'like');
      return;
    }
    setLocalSynonyms(
      localSynonyms.map(s => {
        if (s.id === synonymId && s.approved) {
          const wasDisliked = s.dislikedBy?.includes(currentUser);
          const newDislikedBy = wasDisliked
            ? s.dislikedBy.filter(u => u !== currentUser)
            : (s.dislikedBy || []);
          const newDislikes = wasDisliked ? (s.dislikes || 0) - 1 : (s.dislikes || 0);

          return {
            ...s,
            likes: (s.likes || 0) + 1,
            likedBy: [...(s.likedBy || []), currentUser],
            dislikes: newDislikes,
            dislikedBy: newDislikedBy
          };
        }
        return s;
      })
    );
  };

  const handleDislikeConfirmedSynonym = async (synonymId) => {
    if (onVoteSynonym) {
      onVoteSynonym(synonymId, 'dislike');
      return;
    }
    setLocalSynonyms(
      localSynonyms.map(s => {
        if (s.id === synonymId && s.approved) {
          const wasLiked = s.likedBy?.includes(currentUser);
          const newLikedBy = wasLiked
            ? s.likedBy.filter(u => u !== currentUser)
            : (s.likedBy || []);
          const newLikes = wasLiked ? (s.likes || 0) - 1 : (s.likes || 0);

          return {
            ...s,
            dislikes: (s.dislikes || 0) + 1,
            dislikedBy: [...(s.dislikedBy || []), currentUser],
            likes: newLikes,
            likedBy: newLikedBy
          };
        }
        return s;
      })
    );
  };

  const handleAddCommentToConfirmedSynonym = async (synonymId) => {
    const comment = commentText[`confirmed_${synonymId}`];
    if (comment && comment.trim()) {
      if (onCommentSynonym) {
        onCommentSynonym(synonymId, comment.trim());
        setCommentText({ ...commentText, [`confirmed_${synonymId}`]: '' });
        setShowCommentInput({ ...showCommentInput, [`confirmed_${synonymId}`]: false });
        return;
      }
      setLocalSynonyms(
        localSynonyms.map(s =>
          s.id === synonymId && s.approved
            ? {
                ...s,
                comments: [
                  ...(s.comments || []),
                  {
                    author: currentUser,
                    text: comment.trim(),
                    date: new Date().toISOString().split('T')[0]
                  }
                ]
              }
            : s
        )
      );
      setCommentText({ ...commentText, [`confirmed_${synonymId}`]: '' });
      setShowCommentInput({ ...showCommentInput, [`confirmed_${synonymId}`]: false });
    }
  };

  // Handlers for pending synonyms (from others)
  const handleLikeSynonym = async (suggestionId) => {
    if (onVoteSynonym) {
      onVoteSynonym(suggestionId, 'like');
      return;
    }
    setPendingSynonymSuggestions(
      pendingSynonymSuggestions.map(s => {
        if (s.id === suggestionId) {
          // Remove from disliked if previously disliked
          const wasDisliked = s.dislikedBy.includes(currentUser);
          const newDislikedBy = wasDisliked
            ? s.dislikedBy.filter(u => u !== currentUser)
            : s.dislikedBy;
          const newDislikes = wasDisliked ? s.dislikes - 1 : s.dislikes;

          // Add to liked
          return {
            ...s,
            likes: s.likes + 1,
            likedBy: [...s.likedBy, currentUser],
            dislikes: newDislikes,
            dislikedBy: newDislikedBy
          };
        }
        return s;
      })
    );
  };

  const handleDislikeSynonym = async (suggestionId) => {
    if (onVoteSynonym) {
      onVoteSynonym(suggestionId, 'dislike');
      return;
    }
    setPendingSynonymSuggestions(
      pendingSynonymSuggestions.map(s => {
        if (s.id === suggestionId) {
          // Remove from liked if previously liked
          const wasLiked = s.likedBy.includes(currentUser);
          const newLikedBy = wasLiked
            ? s.likedBy.filter(u => u !== currentUser)
            : s.likedBy;
          const newLikes = wasLiked ? s.likes - 1 : s.likes;

          // Add to disliked
          return {
            ...s,
            dislikes: s.dislikes + 1,
            dislikedBy: [...s.dislikedBy, currentUser],
            likes: newLikes,
            likedBy: newLikedBy
          };
        }
        return s;
      })
    );
  };

  const handleAddComment = async (suggestionId) => {
    const comment = commentText[suggestionId];
    if (comment && comment.trim()) {
      if (onCommentSynonym) {
        onCommentSynonym(suggestionId, comment.trim());
        setCommentText({ ...commentText, [suggestionId]: '' });
        setShowCommentInput({ ...showCommentInput, [suggestionId]: false });
        return;
      }
      setPendingSynonymSuggestions(
        pendingSynonymSuggestions.map(s =>
          s.id === suggestionId
            ? {
                ...s,
                comments: [
                  ...s.comments,
                  {
                    author: currentUser,
                    text: comment.trim(),
                    date: new Date().toISOString().split('T')[0]
                  }
                ]
              }
            : s
        )
      );
      setCommentText({ ...commentText, [suggestionId]: '' });
      setShowCommentInput({ ...showCommentInput, [suggestionId]: false });
    }
  };

  // Handlers for own unapproved synonyms
  const handleLikeOwnSynonym = async (synonymId) => {
    if (onVoteSynonym) {
      onVoteSynonym(synonymId, 'like');
      return;
    }
    setLocalSynonyms(
      localSynonyms.map(s => {
        if (s.id === synonymId && !s.approved) {
          const wasDisliked = s.dislikedBy?.includes(currentUser);
          const newDislikedBy = wasDisliked
            ? s.dislikedBy.filter(u => u !== currentUser)
            : (s.dislikedBy || []);
          const newDislikes = wasDisliked ? (s.dislikes || 0) - 1 : (s.dislikes || 0);

          return {
            ...s,
            likes: (s.likes || 0) + 1,
            likedBy: [...(s.likedBy || []), currentUser],
            dislikes: newDislikes,
            dislikedBy: newDislikedBy
          };
        }
        return s;
      })
    );
  };

  const handleDislikeOwnSynonym = async (synonymId) => {
    if (onVoteSynonym) {
      onVoteSynonym(synonymId, 'dislike');
      return;
    }
    setLocalSynonyms(
      localSynonyms.map(s => {
        if (s.id === synonymId && !s.approved) {
          const wasLiked = s.likedBy?.includes(currentUser);
          const newLikedBy = wasLiked
            ? s.likedBy.filter(u => u !== currentUser)
            : (s.likedBy || []);
          const newLikes = wasLiked ? (s.likes || 0) - 1 : (s.likes || 0);

          return {
            ...s,
            dislikes: (s.dislikes || 0) + 1,
            dislikedBy: [...(s.dislikedBy || []), currentUser],
            likes: newLikes,
            likedBy: newLikedBy
          };
        }
        return s;
      })
    );
  };

  const handleAddCommentToOwnSynonym = async (synonymId) => {
    const comment = commentText[`own_${synonymId}`];
    if (comment && comment.trim()) {
      if (onCommentSynonym) {
        onCommentSynonym(synonymId, comment.trim());
        setCommentText({ ...commentText, [`own_${synonymId}`]: '' });
        setShowCommentInput({ ...showCommentInput, [`own_${synonymId}`]: false });
        return;
      }
      setLocalSynonyms(
        localSynonyms.map(s =>
          s.id === synonymId && !s.approved
            ? {
                ...s,
                comments: [
                  ...(s.comments || []),
                  {
                    author: currentUser,
                    text: comment.trim(),
                    date: new Date().toISOString().split('T')[0]
                  }
                ]
              }
            : s
        )
      );
      setCommentText({ ...commentText, [`own_${synonymId}`]: '' });
      setShowCommentInput({ ...showCommentInput, [`own_${synonymId}`]: false });
    }
  };

  // Edit handlers for own synonyms
  const handleEditOwnSynonym = (synonym) => {
    setEditingSynonymId(synonym.id);
    setEditSynonymData({
      term: synonym.term,
      language: synonym.language,
      context: synonym.context || ''
    });
  };

  const handleSaveEditSynonym = () => {
    if (editSynonymData.term.trim()) {
      setLocalSynonyms(
        localSynonyms.map(s =>
          s.id === editingSynonymId
            ? {
                ...s,
                term: editSynonymData.term.trim(),
                language: editSynonymData.language,
                context: editSynonymData.context.trim()
              }
            : s
        )
      );
      setEditingSynonymId(null);
      setEditSynonymData({ term: '', language: 'de', context: '' });
    }
  };

  const handleCancelEditSynonym = () => {
    setEditingSynonymId(null);
    setEditSynonymData({ term: '', language: 'de', context: '' });
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Left: Categories (Tags) Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Typography variant="h6" fontWeight="bold" gutterBottom>
              {copy.categories}
            </Typography>
            <CategoriesManagement
              tags={tags}
              tagDetails={tagDetails}
              availableTags={availableTags}
              onAddTag={onAddTag}
              onApproveTag={onApproveTag}
              onRejectTag={onRejectTag}
              onVoteTag={onVoteTag}
              onCommentTag={onCommentTag}
              onRemoveTag={onRemoveTag}
              currentUser={currentUser}
              userRole={userRole}
            />
          </Paper>
        </Grid>

        {/* Right: Synonyms Section */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 3, height: '100%' }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" fontWeight="bold">
                {copy.synonyms}
              </Typography>
            </Box>

            <Typography variant="body2" color="textSecondary" gutterBottom sx={{ mb: 2 }}>
              {copy.synonymsHelp}
            </Typography>

            {/* Approved Synonyms - with scrolling */}
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                {copy.approvedSynonyms}
              </Typography>
              <Box
                sx={{
                  maxHeight: '350px',
                  overflowY: 'auto',
                  pr: 1,
                  '&::-webkit-scrollbar': {
                    width: '8px',
                  },
                  '&::-webkit-scrollbar-track': {
                    backgroundColor: '#f1f1f1',
                    borderRadius: '4px',
                  },
                  '&::-webkit-scrollbar-thumb': {
                    backgroundColor: '#888',
                    borderRadius: '4px',
                    '&:hover': {
                      backgroundColor: '#555',
                    }
                  }
                }}
              >
                <List dense disablePadding>
                  {localSynonyms
                    .filter(s => s.approved)
                    .map(synonym => (
                      <ListItem key={synonym.id} disablePadding sx={{ mb: 1 }}>
                        <Paper variant="outlined" sx={{ p: 1.5, width: '100%', backgroundColor: '#e8f5e9', borderColor: '#66bb6a' }}>
                          {/* Header row with term, language, and like/dislike */}
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight="bold">
                                {synonym.term}
                              </Typography>
                              <Chip
                                label={synonym.language.toUpperCase()}
                                size="small"
                                sx={{ height: 16, fontSize: '0.6rem' }}
                              />
                              <CheckIcon color="success" fontSize="small" />
                            </Box>

                            {/* Like/Dislike/Comment/Delete buttons */}
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleLikeConfirmedSynonym(synonym.id)}
                                  disabled={synonym.likedBy?.includes(currentUser)}
                                  aria-label={`${copy.likeSynonym}: ${synonym.term}`}
                                  title={copy.likeSynonym}
                                  sx={{
                                    p: 0.3,
                                    color: synonym.likedBy?.includes(currentUser) ? '#4caf50' : 'inherit'
                                  }}
                                >
                                  <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                                <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                                  {synonym.likes || 0}
                                </Typography>
                              </Box>

                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                <IconButton
                                  size="small"
                                  onClick={() => handleDislikeConfirmedSynonym(synonym.id)}
                                  disabled={synonym.dislikedBy?.includes(currentUser)}
                                  aria-label={`${copy.dislikeSynonym}: ${synonym.term}`}
                                  title={copy.dislikeSynonym}
                                  sx={{
                                    p: 0.3,
                                    color: synonym.dislikedBy?.includes(currentUser) ? '#f44336' : 'inherit'
                                  }}
                                >
                                  <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                                <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                                  {synonym.dislikes || 0}
                                </Typography>
                              </Box>

                              {/* Comment button */}
                              <IconButton
                                size="small"
                                onClick={() =>
                                  setShowCommentInput({
                                    ...showCommentInput,
                                    [`confirmed_${synonym.id}`]: !showCommentInput[`confirmed_${synonym.id}`]
                                  })
                                }
                                aria-label={`${copy.addComment}: ${synonym.term}`}
                                title={copy.addComment}
                                sx={{ p: 0.3 }}
                              >
                                <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                              </IconButton>

                              {/* Delete button - only for Data Steward/Admin */}
                              {(userRole === 'data_steward' || userRole === 'admin') && (
                                <IconButton
                                  size="small"
                                  onClick={() => handleDeleteConfirmedSynonymClick(synonym)}
                                  aria-label={`${copy.deleteSynonym}: ${synonym.term}`}
                                  title={copy.deleteSynonym}
                                  sx={{
                                    p: 0.3,
                                    color: 'error.main',
                                    '&:hover': {
                                      backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                    }
                                  }}
                                >
                                  <DeleteIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                </IconButton>
                              )}
                            </Box>
                          </Box>

                          {/* Context and metadata */}
                          {synonym.context && (
                            <Typography variant="caption" color="textSecondary" display="block">
                              {synonym.context}
                            </Typography>
                          )}
                          <Typography variant="caption" color="textSecondary" display="block">
                            {copy.addedByOn
                              .replace('{{user}}', synonym.addedBy)
                              .replace('{{date}}', new Date(synonym.addedDate).toLocaleDateString(locale))}
                          </Typography>
                          {synonym.approvedBy && (
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: synonym.comments?.length > 0 || showCommentInput[`confirmed_${synonym.id}`] ? 1 : 0 }}>
                              {copy.approvedBy.replace('{{user}}', synonym.approvedBy)}
                            </Typography>
                          )}

                          {/* Comment input (collapsible) */}
                          {showCommentInput[`confirmed_${synonym.id}`] && (
                            <Box sx={{ mt: 1 }}>
                              <TextField
                                fullWidth
                                size="small"
                                placeholder={copy.addComment}
                                value={commentText[`confirmed_${synonym.id}`] || ''}
                                onChange={e =>
                                  setCommentText({ ...commentText, [`confirmed_${synonym.id}`]: e.target.value })
                                }
                                sx={{ mb: 0.5 }}
                              />
                              <Button
                                size="small"
                                variant="contained"
                                onClick={() => handleAddCommentToConfirmedSynonym(synonym.id)}
                                disabled={!commentText[`confirmed_${synonym.id}`]?.trim()}
                                sx={{ py: 0.3 }}
                              >
                                {copy.sendComment}
                              </Button>
                            </Box>
                          )}

                          {/* Display existing comments */}
                          {synonym.comments && synonym.comments.length > 0 && (
                            <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #c8e6c9' }}>
                              {synonym.comments.map((comment, idx) => (
                                <Box key={idx} sx={{ mb: 0.5 }}>
                                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                    <Typography variant="caption" fontWeight="bold">
                                      {comment.author}:
                                    </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                      {new Date(comment.date).toLocaleDateString(locale)}
                                  </Typography>
                                  </Box>
                                  <Typography variant="caption">
                                    {comment.text}
                                  </Typography>
                                </Box>
                              ))}
                            </Box>
                          )}
                        </Paper>
                      </ListItem>
                    ))}
                </List>
              </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            {/* Add Synonym Button */}
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              fullWidth
              onClick={() => setShowAddSynonymDialog(true)}
            >
              {copy.suggestSynonym}
            </Button>

            {/* Pending Synonym Suggestions - Moved inside Synonyms section */}
            {(pendingSynonymSuggestions.length > 0 || localSynonyms.filter(s => !s.approved).length > 0) && (
              <Box sx={{ mt: 3 }}>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <InfoOutlinedIcon color="warning" />
                  <Typography variant="subtitle2" fontWeight="bold">
                    {copy.pendingSuggestions}
                  </Typography>
                  <Chip
                    label={pendingSynonymSuggestions.length + localSynonyms.filter(s => !s.approved).length}
                    size="small"
                    color="warning"
                  />
                </Box>

                <Typography variant="caption" color="textSecondary" gutterBottom display="block" sx={{ mb: 2 }}>
                  {copy.pendingHelp}
                </Typography>

                {/* Pending Suggestions from others */}
                {pendingSynonymSuggestions.map(suggestion => (
                  <Paper key={suggestion.id} variant="outlined" sx={{ p: 1.5, backgroundColor: '#fff9e6', borderColor: '#ff9800', mb: 1.5 }}>
                    {/* Header with term and action buttons */}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography variant="body2" fontWeight="bold">
                          {suggestion.term}
                        </Typography>
                        <Chip
                          label={suggestion.language.toUpperCase()}
                          size="small"
                          sx={{ height: 16, fontSize: '0.6rem' }}
                        />
                      </Box>

                      {/* Like/Dislike/Comment buttons on the right */}
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleLikeSynonym(suggestion.id)}
                            disabled={suggestion.likedBy.includes(currentUser)}
                            aria-label={`${copy.likeSynonym}: ${suggestion.term}`}
                            title={copy.likeSynonym}
                            sx={{
                              p: 0.3,
                              color: suggestion.likedBy.includes(currentUser) ? '#4caf50' : 'inherit'
                            }}
                          >
                            <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {suggestion.likes}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                          <IconButton
                            size="small"
                            onClick={() => handleDislikeSynonym(suggestion.id)}
                            disabled={suggestion.dislikedBy.includes(currentUser)}
                            aria-label={`${copy.dislikeSynonym}: ${suggestion.term}`}
                            title={copy.dislikeSynonym}
                            sx={{
                              p: 0.3,
                              color: suggestion.dislikedBy.includes(currentUser) ? '#f44336' : 'inherit'
                            }}
                          >
                            <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                          </IconButton>
                          <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                            {suggestion.dislikes}
                          </Typography>
                        </Box>

                        {/* Comment button */}
                        <IconButton
                          size="small"
                          onClick={() =>
                            setShowCommentInput({
                              ...showCommentInput,
                              [suggestion.id]: !showCommentInput[suggestion.id]
                            })
                          }
                          aria-label={`${copy.addComment}: ${suggestion.term}`}
                          title={copy.addComment}
                          sx={{ p: 0.3 }}
                        >
                          <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                        </IconButton>
                      </Box>
                    </Box>

                    {/* Context and suggested by */}
                    {suggestion.context && (
                      <Typography variant="caption" color="textSecondary" display="block">
                        {copy.context}: {suggestion.context}
                      </Typography>
                    )}
                    <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: suggestion.comments?.length > 0 || showCommentInput[suggestion.id] ? 1 : 0 }}>
                      {copy.suggestedBy.replace('{{user}}', suggestion.suggestedBy)}
                    </Typography>

                    {/* Action buttons - only for Data Stewards/Admins */}
                    {(userRole === 'data_steward' || userRole === 'admin') && (
                      <Box sx={{ display: 'flex', gap: 0.5, mb: 1 }}>
                        <Button
                          size="small"
                          variant="outlined"
                          color="success"
                          startIcon={<CheckIcon />}
                          onClick={() => handleApproveSynonym(suggestion.id)}
                          sx={{ flex: 1, py: 0.3 }}
                        >
                          {copy.approve}
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          startIcon={<CloseIcon />}
                          onClick={() => handleRejectSuggestion(suggestion.id)}
                          sx={{ flex: 1, py: 0.3 }}
                        >
                          {copy.reject}
                        </Button>
                      </Box>
                    )}

                    {/* Comment input (collapsible) */}
                    {showCommentInput[suggestion.id] && (
                      <Box sx={{ mt: 1 }}>
                        <TextField
                          fullWidth
                          size="small"
                          placeholder={copy.addComment}
                          value={commentText[suggestion.id] || ''}
                          onChange={e =>
                            setCommentText({ ...commentText, [suggestion.id]: e.target.value })
                          }
                          sx={{ mb: 0.5 }}
                        />
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleAddComment(suggestion.id)}
                          disabled={!commentText[suggestion.id]?.trim()}
                          sx={{ py: 0.3 }}
                        >
                          {copy.sendComment}
                        </Button>
                      </Box>
                    )}

                    {/* Display existing comments */}
                    {suggestion.comments && suggestion.comments.length > 0 && (
                      <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                        {suggestion.comments.map((comment, idx) => (
                          <Box key={idx} sx={{ mb: 0.5 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                              <Typography variant="caption" fontWeight="bold">
                                {comment.author}:
                              </Typography>
                              <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                {new Date(comment.date).toLocaleDateString(locale)}
                              </Typography>
                            </Box>
                            <Typography variant="caption">
                              {comment.text}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    )}
                  </Paper>
                ))}

                {/* User's own unapproved synonyms */}
                {localSynonyms
                  .filter(s => !s.approved && s.addedBy === currentUser)
                  .map(synonym => {
                    const isOwnSynonym = synonym.addedBy === currentUser;
                    return (
                      <Paper key={synonym.id} variant="outlined" sx={{ p: 1.5, backgroundColor: '#fff9e6', borderColor: '#ff9800', mb: 1.5 }}>
                        {editingSynonymId === synonym.id ? (
                          /* Edit Mode */
                          <Box>
                            <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                              {copy.editSynonym}
                            </Typography>
                            <TextField
                              fullWidth
                              size="small"
                              label={copy.synonymField}
                              value={editSynonymData.term}
                              onChange={(e) => setEditSynonymData({ ...editSynonymData, term: e.target.value })}
                              sx={{ mb: 1.5 }}
                            />
                            <Autocomplete
                              options={['de', 'en', 'pt', 'fr', 'es']}
                              value={editSynonymData.language}
                              onChange={(e, value) => setEditSynonymData({ ...editSynonymData, language: value || 'de' })}
                              renderInput={(params) => (
                                <TextField {...params} label={copy.languageField} size="small" />
                              )}
                              sx={{ mb: 1.5 }}
                            />
                            <TextField
                              fullWidth
                              size="small"
                              label={copy.optionalContextField}
                              value={editSynonymData.context}
                              onChange={(e) => setEditSynonymData({ ...editSynonymData, context: e.target.value })}
                              multiline
                              rows={2}
                              sx={{ mb: 1.5 }}
                            />
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Button
                                size="small"
                                variant="contained"
                                startIcon={<SaveIcon />}
                                onClick={handleSaveEditSynonym}
                                disabled={!editSynonymData.term.trim()}
                                sx={{ flex: 1 }}
                              >
                                {copy.save}
                              </Button>
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={handleCancelEditSynonym}
                                sx={{ flex: 1 }}
                              >
                                {copy.cancel}
                              </Button>
                            </Box>
                          </Box>
                        ) : (
                          /* Normal View */
                          <>
                            {/* Header with term and action buttons */}
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight="bold">
                                  {synonym.term}
                                </Typography>
                                <Chip
                                  label={synonym.language.toUpperCase()}
                                  size="small"
                                  sx={{ height: 16, fontSize: '0.6rem' }}
                                />
                              </Box>

                              {/* Like/Dislike/Comment/Edit/Delete buttons */}
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleLikeOwnSynonym(synonym.id)}
                                    disabled={isOwnSynonym || synonym.likedBy?.includes(currentUser)}
                                    aria-label={`${copy.likeOwnSuggestion}: ${synonym.term}`}
                                    title={copy.likeOwnSuggestion}
                                    sx={{
                                      p: 0.3,
                                      color: synonym.likedBy?.includes(currentUser) ? '#4caf50' : 'inherit'
                                    }}
                                  >
                                    <ThumbUpIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                  <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                                    {synonym.likes || 0}
                                  </Typography>
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.3 }}>
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDislikeOwnSynonym(synonym.id)}
                                    disabled={isOwnSynonym || synonym.dislikedBy?.includes(currentUser)}
                                    aria-label={`${copy.dislikeOwnSuggestion}: ${synonym.term}`}
                                    title={copy.dislikeOwnSuggestion}
                                    sx={{
                                      p: 0.3,
                                      color: synonym.dislikedBy?.includes(currentUser) ? '#f44336' : 'inherit'
                                    }}
                                  >
                                    <ThumbDownIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                  <Typography variant="caption" sx={{ minWidth: 12, fontSize: '0.7rem' }}>
                                    {synonym.dislikes || 0}
                                  </Typography>
                                </Box>

                                {/* Comment button */}
                                <IconButton
                                  size="small"
                                  onClick={() =>
                                    setShowCommentInput({
                                      ...showCommentInput,
                                      [`own_${synonym.id}`]: !showCommentInput[`own_${synonym.id}`]
                                    })
                                  }
                                  aria-label={`${copy.addComment}: ${synonym.term}`}
                                  title={copy.addComment}
                                  sx={{ p: 0.3 }}
                                >
                                  <CommentIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                </IconButton>

                                {/* Edit button - only for the creator */}
                                {isOwnSynonym && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleEditOwnSynonym(synonym)}
                                    aria-label={`${copy.editOwnSuggestion}: ${synonym.term}`}
                                    title={copy.editOwnSuggestion}
                                    sx={{ p: 0.3 }}
                                  >
                                    <EditIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                )}

                                {/* Delete button - only for the creator */}
                                {isOwnSynonym && (
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveSynonym(synonym.id)}
                                    aria-label={`${copy.deleteOwnSuggestion}: ${synonym.term}`}
                                    title={copy.deleteOwnSuggestion}
                                    sx={{
                                      p: 0.3,
                                      color: 'error.main',
                                      '&:hover': {
                                        backgroundColor: 'rgba(244, 67, 54, 0.08)'
                                      }
                                    }}
                                  >
                                    <DeleteIcon fontSize="small" sx={{ fontSize: '0.9rem' }} />
                                  </IconButton>
                                )}
                              </Box>
                            </Box>

                            {/* Context and suggested by */}
                            {synonym.context && (
                              <Typography variant="caption" color="textSecondary" display="block">
                                {copy.context}: {synonym.context}
                              </Typography>
                            )}
                            <Typography variant="caption" color="textSecondary" display="block" sx={{ mb: synonym.comments?.length > 0 || showCommentInput[`own_${synonym.id}`] ? 1 : 0 }}>
                              {copy.suggestedBy.replace('{{user}}', synonym.addedBy)}
                            </Typography>
                          </>
                        )}

                        {/* Comment input (collapsible) */}
                        {showCommentInput[`own_${synonym.id}`] && (
                          <Box sx={{ mt: 1 }}>
                            <TextField
                              fullWidth
                              size="small"
                              placeholder={copy.addComment}
                              value={commentText[`own_${synonym.id}`] || ''}
                              onChange={e =>
                                setCommentText({ ...commentText, [`own_${synonym.id}`]: e.target.value })
                              }
                              sx={{ mb: 0.5 }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              onClick={() => handleAddCommentToOwnSynonym(synonym.id)}
                              disabled={!commentText[`own_${synonym.id}`]?.trim()}
                              sx={{ py: 0.3 }}
                            >
                              {copy.sendComment}
                            </Button>
                          </Box>
                        )}

                        {/* Display existing comments */}
                        {synonym.comments && synonym.comments.length > 0 && (
                          <Box sx={{ mt: 1, pt: 1, borderTop: '1px solid #e0e0e0' }}>
                            {synonym.comments.map((comment, idx) => (
                              <Box key={idx} sx={{ mb: 0.5 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                                  <Typography variant="caption" fontWeight="bold">
                                    {comment.author}:
                                  </Typography>
                                  <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.65rem' }}>
                                    {new Date(comment.date).toLocaleDateString(locale)}
                                  </Typography>
                                </Box>
                                <Typography variant="caption">
                                  {comment.text}
                                </Typography>
                              </Box>
                            ))}
                          </Box>
                        )}
                      </Paper>
                    );
                  })}
              </Box>
            )}
          </Paper>
        </Grid>
      </Grid>

      {/* Add Synonym Dialog */}
      <Dialog open={showAddSynonymDialog} onClose={() => setShowAddSynonymDialog(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{copy.proposeSynonym}</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label={copy.synonymField}
              placeholder={copy.synonymPlaceholder}
              value={newSynonym}
              onChange={(e) => setNewSynonym(e.target.value)}
              sx={{ mb: 2 }}
              autoFocus
            />
            <Autocomplete
              options={['de', 'en', 'pt', 'fr', 'es']}
              value={synonymLanguage}
              onChange={(e, value) => setSynonymLanguage(value || 'de')}
              renderInput={(params) => (
                <TextField {...params} label={copy.languageField} />
              )}
              sx={{ mb: 2 }}
            />
            <TextField
              fullWidth
              label={copy.optionalContextField}
              placeholder={copy.contextPlaceholder}
              value={synonymContext}
              onChange={(e) => setSynonymContext(e.target.value)}
              multiline
              rows={2}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              <Typography variant="caption">
                {copy.submitHelp}
              </Typography>
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddSynonymDialog(false)}>{copy.cancel}</Button>
          <Button
            onClick={handleAddSynonym}
            variant="contained"
            disabled={!newSynonym.trim()}
          >
            {copy.submit}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {copy.deleteTitle}
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Typography variant="body2">
              {copy.deletePrompt}
            </Typography>
          </Alert>
          {synonymToDelete && (
            <Box sx={{ p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
              <Typography variant="body2" fontWeight="bold">
                {synonymToDelete.term}
              </Typography>
              <Typography variant="caption" color="textSecondary">
                {copy.languageLabel.replace('{{language}}', synonymToDelete.language.toUpperCase())}
              </Typography>
              {synonymToDelete.context && (
                <Typography variant="caption" display="block" color="textSecondary">
                  {copy.context}: {synonymToDelete.context}
                </Typography>
              )}
              <Typography variant="caption" display="block" color="textSecondary" sx={{ mt: 1 }}>
                {copy.addedBy
                  .replace('{{user}}', synonymToDelete.addedBy)
                  .replace('{{date}}', synonymToDelete.addedDate)}
              </Typography>
            </Box>
          )}
          <Typography variant="caption" color="textSecondary" sx={{ mt: 2, display: 'block' }}>
            {copy.cannotUndo}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete}>
            {copy.cancel}
          </Button>
          <Button
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            startIcon={<DeleteIcon />}
          >
            {copy.deleteSynonym}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default TagsSynonymsDetail;
